import { useState, useEffect, useCallback } from 'react'
import { Table, Card, Button, Space, Modal, Form, Input, InputNumber, message, Popconfirm, Tag } from 'antd'
import { PlusOutlined, SearchOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { auxiliaryApi } from '../services/api'
import { inventoryApi } from '../services/api'

const { confirm } = Modal

interface AuxiliaryProduct {
  id: number
  name: string
  code: string
  unit: string
  enterprise_price: number  // 采购价
  product_type: number
  created_at: string
  stock_quantity?: number  // 库存数量
}

export default function Auxiliary() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<AuxiliaryProduct[]>([])
  const [stockMap, setStockMap] = useState<Record<number, number>>({})
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [keyword, setKeyword] = useState('')
  const [modalVisible, setModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<AuxiliaryProduct | null>(null)
  const [form] = Form.useForm()

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await auxiliaryApi.getList({ page, page_size: pageSize, keyword })
      if (res.code === 200) {
        const list = res.data?.list || []
        setData(list)
        setTotal(Number(res.data?.total) || 0)
        
        // 加载库存数量
        if (list.length > 0) {
          const ids = list.map((item: AuxiliaryProduct) => item.id).join(',')
          try {
            console.log('正在查询库存，产品IDs:', ids)
            const stockRes = await inventoryApi.getSummary({ warehouse_id: 6 })
            console.log('库存查询结果:', stockRes)
            if (stockRes.code === 0 || stockRes.code === 200) {
              const map: Record<number, number> = {}
              ;(stockRes.data || []).forEach((item: any) => {
                map[item.product_id] = item.total_quantity || 0
              })
              console.log('库存Map:', map)
              setStockMap(map)
              // 更新 data 中的库存数量
              setData(list.map((p: AuxiliaryProduct) => ({ ...p, stock_quantity: map[p.id] || 0 })))
            }
          } catch (e) {
            console.error('加载库存失败', e)
          }
        }
      }
    } catch {
      message.error('加载辅料列表失败')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, keyword])

  useEffect(() => { loadData() }, [loadData])

  const handleAdd = () => {
    setEditingItem(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record: AuxiliaryProduct) => {
    setEditingItem(record)
    form.setFieldsValue({
      name: record.name,
      unit: record.unit,
      enterprise_price: Number(record.enterprise_price),
    })
    setModalVisible(true)
  }

  const handleDelete = async (id: number) => {
    try {
      const res = await auxiliaryApi.delete(id)
      if (res.code === 200) {
        message.success('删除成功')
        loadData()
      } else {
        message.error(res.message || '删除失败')
      }
    } catch {
      message.error('删除失败')
    }
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      // 辅料不需要 personal_price，统一传 0
      const submitData = { ...values, personal_price: 0, product_type: 2 }
      if (editingItem) {
        const res = await auxiliaryApi.update(editingItem.id, submitData)
        if (res.code === 200) {
          message.success('更新成功')
          setModalVisible(false)
          loadData()
        } else {
          message.error(res.message || '更新失败')
        }
      } else {
        const res = await auxiliaryApi.create(submitData)
        if (res.code === 200) {
          message.success('添加成功')
          setModalVisible(false)
          loadData()
        } else {
          message.error(res.message || '添加失败')
        }
      }
    } catch (err: any) {
      if (err?.errorFields) return
      message.error(editingItem ? '更新失败' : '添加失败')
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: '编码', dataIndex: 'code', key: 'code', width: 100 },
    { title: '单位', dataIndex: 'unit', key: 'unit', width: 80 },
    {
      title: '采购价(元)',
      dataIndex: 'enterprise_price',
      key: 'enterprise_price',
      width: 100,
      render: (v: number) => `¥${Number(v || 0).toFixed(2)}`
    },
    {
      title: '库存数量',
      dataIndex: 'stock_quantity',
      key: 'stock_quantity',
      width: 100,
      render: (v: number) => {
        if (v === undefined || v === null) return '-'
        return v > 0 ? <span style={{ color: '#52c41a' }}>{v}</span> : <Tag color="red">缺货</Tag>
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_: any, record: AuxiliaryProduct) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除该辅料？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Space>
            <Input
              placeholder="搜索辅料名称"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onPressEnter={() => { setPage(1); loadData() }}
              style={{ width: 200 }}
              allowClear
            />
            <Button icon={<SearchOutlined />} onClick={() => { setPage(1); loadData() }}>查询</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { setKeyword(''); setPage(1); loadData() }}>重置</Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增辅料</Button>
        </div>

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t: number) => `共 ${t} 条`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
        />
      </Card>

      <Modal
        title={editingItem ? '编辑辅料' : '新增辅料'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={form} labelCol={{ span: 5 }} wrapperCol={{ span: 17 }} style={{ marginTop: 24 }}>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入辅料名称' }]}>
            <Input placeholder="如：食盐、酱油" />
          </Form.Item>
          <Form.Item name="unit" label="单位" rules={[{ required: true, message: '请输入单位' }]}>
            <Input placeholder="如：个、米、克、包、瓶" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item name="enterprise_price" label="采购价">
            <InputNumber min={0} step={0.01} precision={2} addonBefore="¥" style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
